import type { Theme } from '@mui/material';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PropertyList } from '../../../../../components/atoms/property-list';
import { PropertyListItem } from '../../../../../components/atoms/property-list-item';
import { DiscountTemplate } from '../../../api/types/discount';
import { Host } from '../../../api/types/host';
import AllowedInviteToParking from '../allowed-invite-to-parking';

import { Can } from '@/permissions/can';

interface Props {
  host: Host;
  discounts: DiscountTemplate[];
  handleOpenUpdateDiscountDialog: () => void;
}

export const DiscountInfo = ({ host, handleOpenUpdateDiscountDialog }: Props) => {
  const { t } = useTranslation(['host', 'common']);

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const align = mdDown ? 'vertical' : 'horizontal';

  return (
    <Card variant="outlined">
      <CardHeader
        title={t('host:discount.cardTitle')}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography sx={{ mr: 1 }} variant="body2">
              {t('host:discount.canInviteToParking')}:
            </Typography>
            <AllowedInviteToParking
              allowInviteToParking={host?.allowInviteToParking}
              hostId={host?.id}
            />
            <Can I="manageDiscount" a="Hosts" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      disabled={!host.allowInviteToParking || !allowed}
                      onClick={handleOpenUpdateDiscountDialog}>
                      {t('host:discount.assignDiscount')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          </Box>
        }
      />
      <Divider />
      <PropertyList>
        {host.discountTemplates.length > 0 ? (
          host.discountTemplates.map((el, i) => (
            <PropertyListItem
              key={el.id}
              align={align}
              label={`${t('host:discount.discount')} #${i + 1}`}
              value={el.name}
            />
          ))
        ) : (
          <Typography sx={{ p: 2, px: 3 }}>{t('host:discount.thereIsNoDiscounts')}</Typography>
        )}
      </PropertyList>
    </Card>
  );
};
